import axios from "@getbread/api-client-axios";
import { QueryClient, type QueryClientConfig, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig, Method } from "axios";
import isEmpty from "ramda/es/isEmpty";
import { InvalidJwtApiError } from "./api-client/authentication";
import type { FCWithChildren } from "./api-client/resources/types/sharedTypes";
import { logger } from "./logger";
import { alloyIovationBlackboxStorageKey } from "./utils/alloyIovation";
import { getConfig } from "./utils/getConfig";
import { getItem } from "./utils/localStorage";
import { getItem as getSessionItem } from "./utils/sessionStorage";

export const jwtStorageKey = "bread-member-portal-jwt";

export const buildAuthorizationHeader = (jwt = getItem<string>(jwtStorageKey)) => ({
	authorization: `Bearer ${jwt}`,
});

const logRequest = (config: InternalAxiosRequestConfig) => {
	logger.debug({
		msg: "--> API Request",
		method: config.method as Method,
		url: config.url!,
	});
};

const logResponse = (response: AxiosResponse): AxiosResponse => {
	logger.debug({
		method: response.config.method,
		msg: "<-- API Response",
		status: response.status,
		url: response.config.url,
	});
	return response;
};

const NOT_FOUND_RESPONSE_CODES = [404, 410];

const logErrorResponse = (response: AxiosResponse): AxiosResponse => {
	const errorMessage = {
		method: response.config.method,
		msg: "<-- API Response",
		status: response.status,
		url: response.config.url,
		responseHeaders: response.headers,
		requestHeaders: response.config.headers,
	};
	NOT_FOUND_RESPONSE_CODES.includes(response.status)
		? logger.info(errorMessage)
		: logger.error(errorMessage);
	return response;
};

export const queryClientConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			// don't do API requests for cache updating when the window regains focus
			refetchOnWindowFocus: false,
			// always consider an API request "fresh", and never update it in the background
			staleTime: Infinity,
			// don't bother retrying API requests, just fail the first time
			retry: false,
		},
	},
};

const mpQueryClient = new QueryClient(queryClientConfig);

const getServiceNameFromUrl = (url?: string) => {
	if (!url) return undefined;
	const serviceNameMatch = /\/api\/(?<serviceName>[A-Za-z0-9\-_]+)/.exec(url);
	return serviceNameMatch?.groups?.serviceName;
};

export const apiRequestCustomizer = (config: InternalAxiosRequestConfig) => {
	config.baseURL = getConfig("apiurl");

	if (getServiceNameFromUrl(config.url) === "auth") {
		config.headers["X-Bread-App-ID"] = getConfig("appid");
	}

	if (config.url === "/api/auth/login" && config.method?.toUpperCase() === "POST") {
		const iovationData = getSessionItem<{ iovationBlackboxId?: string }>(
			alloyIovationBlackboxStorageKey,
		);

		if (iovationData?.iovationBlackboxId) {
			config.headers["Iovation-Blackbox-ID"] = iovationData.iovationBlackboxId;
		}
	}

	const jwt = getItem<string>(jwtStorageKey);
	if (!config.headers.authorization && jwt) {
		config.headers.authorization = `Bearer ${jwt}`;
	}
	logRequest(config);

	return config;
};

axios.interceptors.request.use(apiRequestCustomizer);

axios.interceptors.response.use(logResponse, (error: AxiosError) => {
	if (error.response) {
		logErrorResponse(error.response);
	}
	// Auth calls have special error handling in authentication.ts, so don't transform here.
	if (error.response?.status === 401 && getServiceNameFromUrl(error.config?.url) !== "auth") {
		throw new InvalidJwtApiError();
	} else if (!isEmpty(error.response?.data)) {
		// unwrap axios error response data
		throw error.response?.data;
	} else if (error.response) {
		// unwrap axios response
		// eslint-disable-next-line @typescript-eslint/only-throw-error
		throw error.response;
	} else {
		throw error;
	}
});

export const ApiClientProvider: FCWithChildren<{ queryClient?: QueryClient }> = ({
	queryClient = mpQueryClient,
	children,
}) => (
	<QueryClientProvider client={queryClient}>
		<>
			{children}
			<ReactQueryDevtools initialIsOpen={false} />
		</>
	</QueryClientProvider>
);
